import { create } from "zustand";
import { OthersStoreProps } from "./interface/others-store.interface";
import {
  createOthersInformation,
  deleteOtherInformation,
  getAllDistinctColumns,
  getAllDistinctColumnsFillEmission,
  getAllFillEmission,
  getAllReportVocEmission,
  getInformationDistinctYear,
  getMasterDataEmission,
  getOthersInformation,
  updateOthersFillEmission,
  updateOthersInformation,
} from "src/services/others.service";
import { OthersInformationBody, PatchOthersInformationBody, QueryOthersInformation } from "src/entity/others/information";
import {
  FillEmissionObject,
  FillSamplingObject,
  FilterOthersFillEmission,
  FilterOthersReportVocsEmission,
  OthersEmission,
  RawResponseOthersFillEmission,
  ReportMonthData,
  ReportMonthTotalData,
  ReportVocsObject,
  ResponseOthersFillEmission,
  ResponseOthersFillSamplingResult,
  ResponseOthersReportVocs,
  UpdateOthersFillEmission,
} from "src/entity/others/fill-emission";
import { UnitAllEnum } from "src/entity/common/unit.constant";
import { CommonError } from "src/entity/common/menus-enum.enum";
import { MONTHS_LISTS } from "src/entity/common/time.constant";
import { KeyofMonthly } from "src/entity/common/global.entity";
import { parseAndFormatScientificNumber } from "src/utils/process-number";
import { ReportUnitValue } from "src/entity/others/report-voc-emission.entity";
import { CalculationMethodOthersEnum } from "src/entity/common/common.constant";

const getValueFromObjectMonth = (objectMonth: ReportMonthData, selectedUnit: UnitAllEnum) => {
  if (objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue] != null)
    return parseAndFormatScientificNumber(`${objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
  else if (objectMonth[`value${selectedUnit}` as keyof ReportUnitValue] != null)
    return parseAndFormatScientificNumber(`${objectMonth[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
};

const getTotalValueFromObjectMonth = (objectMonth: ReportMonthTotalData, selectedUnit: UnitAllEnum) => {
  if (objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue] != null)
    return parseAndFormatScientificNumber(`${objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
  else if (objectMonth[`value${selectedUnit}` as keyof ReportUnitValue] != null)
    return parseAndFormatScientificNumber(`${objectMonth[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
};

export const othersStore = create<OthersStoreProps>((set) => ({
  loading: {
    mainData: false,
    distinctColumn: false,
    distinctYear: false,
    create: false,
    update: false,
    delete: false,
    fillEmission: false,
    distinctFillEmission: false,
    reportVoc: false,
    loadingAll: false,
  },
  dataOthers: {
    data: [],
    total: 0,
  },
  dataDistinctYear: [],
  dataFilterColumns: {
    area: [],
    sourceName: [],
  },
  dataFilterColFillEmission: {
    area: [],
    sourceName: [],
    emission: [],
  },
  dataFillEmission: {
    data: [],
    total: 0,
  },
  dataStackSampling: {
    data: [],
    total: 0,
  },
  error: null,
  dataMasterEmissionObject: [],

  dataReportVoc: {
    data: [],
    summary: [],
    total: 0,
  },
  setLoadingAll: (load: boolean) => {
    set((state) => ({ loading: { ...state.loading, loadingAll: load } }));
  },
  getMasterEmissionObject: async () => {
    try {
      const response = await getMasterDataEmission();
      set(() => ({ dataMasterEmissionObject: response.data?.data || [] }));
    } catch (error) {
      console.log();
    }
  },
  clearError: () => {
    set(() => ({ error: null }));
  },
  getInformationDistinctYear: async () => {
    set((state) => ({ loading: { ...state.loading, distinctYear: true } }));
    try {
      const res = await getInformationDistinctYear();
      const rawData = res.data?.data || [];

      set(() => ({ dataDistinctYear: rawData }));
    } catch (err: any) {
      console.error("getInformationDistinctYear : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, distinctYear: false } }));
    }
  },
  createOthersInformation: async (body: OthersInformationBody) => {
    set((state) => ({ loading: { ...state.loading, create: true } }));
    try {
      await createOthersInformation(body);
    } catch (err: any) {
      console.error("createOthersInformation : ", err);
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ error: message || CommonError.SOMETHING_WRONG }));
      return message;
    } finally {
      set((state) => ({ loading: { ...state.loading, create: false } }));
    }
  },
  updateOthersInformation: async (body: PatchOthersInformationBody) => {
    set((state) => ({ loading: { ...state.loading, update: true } }));
    try {
      await updateOthersInformation(body);
    } catch (err: any) {
      console.error("updateOthersInformation : ", err);
      const message = err?.response?.data?.message || err?.message;
      set(() => ({ error: message || CommonError.SOMETHING_WRONG }));
      return message;
    } finally {
      set((state) => ({ loading: { ...state.loading, update: false } }));
    }
  },
  getOthersInformation: async (params: QueryOthersInformation) => {
    set((state) => ({ loading: { ...state.loading, mainData: true } }));
    try {
      const res = await getOthersInformation(params);
      const rawData = res.data?.data || {
        data: [],
        total: 0,
      };

      set(() => ({ dataOthers: rawData }));
    } catch (err: any) {
      console.error("getOthersInformation : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, mainData: false } }));
    }
  },
  deleteOtherInformation: async (_id: string) => {
    set((state) => ({ loading: { ...state.loading, delete: true } }));
    try {
      await deleteOtherInformation(_id);
    } catch (err: any) {
      console.error("deleteOtherInformation : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, delete: false } }));
    }
  },
  getAllDistinctColumns: async (year: string) => {
    set((state) => ({ loading: { ...state.loading, distinctColumn: true } }));
    try {
      const res = await getAllDistinctColumns({ year });
      const rawData = res.data?.data || {
        area: [],
        sourceName: [],
      };

      set(() => ({ dataFilterColumns: rawData }));
    } catch (err: any) {
      console.error("getAllDistinctColumns : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, distinctColumn: false } }));
    }
  },

  getAllDistinctColFillEmission: async (
    year: string,
    calculationMethod?: CalculationMethodOthersEnum.STACK_SAMPLING | CalculationMethodOthersEnum.FILL_EMISSION,
  ) => {
    set((state) => ({ loading: { ...state.loading, distinctFillEmission: true } }));
    try {
      const filter: {
        year: string;
        calculationMethod?: CalculationMethodOthersEnum.STACK_SAMPLING | CalculationMethodOthersEnum.FILL_EMISSION;
      } = { year };
      if (calculationMethod) {
        filter["calculationMethod"] = calculationMethod;
      }
      const res = await getAllDistinctColumnsFillEmission(filter);
      const rawData = res.data?.data || {
        area: [],
        sourceName: [],
        emission: [],
      };

      set(() => ({ dataFilterColFillEmission: rawData }));
    } catch (err: any) {
      console.error("getAllDistinctColFillEmission : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, distinctFillEmission: false } }));
    }
  },
  getOthersFillEmission: async (params: FilterOthersFillEmission) => {
    set((state) => ({ loading: { ...state.loading, fillEmission: true } }));
    try {
      const res = await getAllFillEmission(params);

      const rawData = res.data?.data;
      const transformArray: ResponseOthersFillEmission[] = [];
      const paramsEmission = params.emission;
      const isEmissionFilter = Array.isArray(paramsEmission) && paramsEmission.length > 0;
      rawData.data.forEach((item: RawResponseOthersFillEmission, index: number) => {
        const cloneEmission = { ...item.emission };
        const listActiveEmission = [];
        for (const key in item.emission) {
          if (item.emission[key as keyof OthersEmission].active === true) {
            if (isEmissionFilter) {
              if (paramsEmission.includes(key)) listActiveEmission.push(key);
              else delete item.emission[key as keyof typeof item.emission];
            } else listActiveEmission.push(key);
          }
        }

        listActiveEmission.forEach((activeKey) => {
          const obj: Partial<ResponseOthersFillEmission> = {
            ...item,
            cloneEmission,
            no: index + 1,
            activeEmission: activeKey.toUpperCase(),
            unit: UnitAllEnum.KG,
          };
          for (const month of MONTHS_LISTS) {
            obj[month as keyof FillEmissionObject] =
              item.emission[activeKey as keyof typeof item.emission].fillEmission[month as keyof FillEmissionObject];
          }
          transformArray.push(obj as ResponseOthersFillEmission);
        });
      });

      rawData.data = transformArray;
      // console.log("transformArray :: ", transformArray);

      set(() => ({ dataFillEmission: rawData }));
    } catch (err: any) {
      console.error("getOthersFillEmission : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, fillEmission: false } }));
    }
  },

  getOthersStackSampling: async (params: FilterOthersFillEmission): Promise<void> => {
    set((state) => ({ loading: { ...state.loading, fillEmission: true } }));
    try {
      const stackSamplingParams = {
        ...params,
        calculationMethod: CalculationMethodOthersEnum.STACK_SAMPLING,
      };

      const res = await getAllFillEmission(stackSamplingParams);
      const rawData = res.data?.data;

      const paramsEmission = stackSamplingParams.emission;
      const isEmissionFilter = Array.isArray(paramsEmission) && paramsEmission.length > 0;

      const dataFormatted: ResponseOthersFillSamplingResult[] = [];

      rawData?.data.forEach((item: RawResponseOthersFillEmission, index: number) => {
        for (const key in item.emission) {
          if (item.emission[key as keyof OthersEmission].active === true) {
            if (isEmissionFilter && !paramsEmission.includes(key)) {
              continue;
            }

            const fillSamplingResult = item?.emission?.[key as keyof OthersEmission]?.fillSamplingResult;

            if (fillSamplingResult) {
              const orderedKeys = ["flowRate", "concentration", "molecularWeight", "operatingHour", "reference"];

              const sortedKeys = orderedKeys.filter((orderedKey) => fillSamplingResult.hasOwnProperty(orderedKey));

              const remainingKeys = Object.keys(fillSamplingResult)
                .filter((key) => !orderedKeys.includes(key))
                .sort();

              const finalKeys = [...sortedKeys, ...remainingKeys];

              for (const keySampling of finalKeys) {
                const obj: ResponseOthersFillSamplingResult = {
                  ...item,
                  no: index + 1,
                  activeEmission: key.toUpperCase(),
                  samplingResult: keySampling,
                  unit: UnitAllEnum.KG,
                } as ResponseOthersFillSamplingResult;

                for (const month of MONTHS_LISTS) {
                  (obj as any)[month] =
                    fillSamplingResult[keySampling as keyof FillSamplingObject]?.[month as keyof FillEmissionObject] || 0;
                }

                dataFormatted.push(obj);
              }
            }
          }
        }
      });

      rawData.data = dataFormatted;

      set(() => ({ dataStackSampling: rawData }));
    } catch (err: any) {
      console.error("getOthersStackSampling Error:", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, fillEmission: false } }));
    }
  },

  updateOthersFillEmission: async (_id: string, body: UpdateOthersFillEmission) => {
    set((state) => ({ loading: { ...state.loading, update: true } }));
    try {
      body.calculationMethod = CalculationMethodOthersEnum.FILL_EMISSION;
      await updateOthersFillEmission(_id, body);
    } catch (err: any) {
      set(() => ({ error: err?.response?.data?.message || err?.message || CommonError.SOMETHING_WRONG }));
      console.error("updateOthersFillEmission : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, update: false } }));
    }
  },

  updateOthersFillSamplingResult: async (_id: string, body: UpdateOthersFillEmission) => {
    set((state) => ({ loading: { ...state.loading, update: true } }));
    try {
      body.calculationMethod = CalculationMethodOthersEnum.STACK_SAMPLING;
      await updateOthersFillEmission(_id, body);
    } catch (err: any) {
      set(() => ({ error: err?.response?.data?.message || err?.message || CommonError.SOMETHING_WRONG }));
      console.error("updateOthersFillSamplingResult : ", err);
    } finally {
      set((state) => ({ loading: { ...state.loading, update: false } }));
    }
  },

  getAllReportVocEmission: async (params: FilterOthersReportVocsEmission) => {
    // console.log("Params : ", params);
    set((state) => ({ loading: { ...state.loading, reportVoc: true } }));
    try {
      const res = await getAllReportVocEmission(params);

      const rawData = res.data?.data;
      const transformArray: ResponseOthersReportVocs[] = [];
      rawData.data.forEach((item: RawResponseOthersFillEmission, index: number) => {
        if (params.emission?.toUpperCase() !== "ALL") {
          for (const key in item.emission) {
            if (params.emission?.toLowerCase() != key) delete item.vocEmission[key as keyof typeof item.emission];
          }
        }
        const listActiveEmission = [];
        if (params.emission?.toUpperCase() === "ALL") {
          for (const key in item.emission) {
            if (item.emission[key as keyof OthersEmission].active === true) {
              listActiveEmission.push(key);
            }
          }
        } else {
          for (const key in item.emission) {
            if (item.emission[key as keyof OthersEmission].active === true && key === params.emission?.toLowerCase()) {
              listActiveEmission.push(key);
            }
          }
        }

        listActiveEmission.forEach((activeKey) => {
          // console.log("ISSUE item : ", item.vocEmission[activeKey as keyof typeof item.emission]);
          const obj: Partial<ResponseOthersReportVocs> = {
            ...item,
            no: index + 1,
            activeEmission: activeKey.toUpperCase(),
            unit: UnitAllEnum.KG,
            total: item.vocEmission[activeKey as keyof typeof item.emission].totalValue,
          };
          for (const month of MONTHS_LISTS) {
            obj[month as keyof ReportVocsObject] = item.vocEmission[activeKey as keyof typeof item.emission][month as keyof KeyofMonthly];
          }
          transformArray.push(obj as ResponseOthersReportVocs);
        });
      });

      rawData.data = transformArray;
      // console.log("transformArray :: ", transformArray);

      set(() => ({ dataReportVoc: rawData }));
    } catch (err: any) {
      console.error("getAllReportVocEmission : ", err);
      set(() => ({
        dataReportVoc: {
          data: [],
          summary: [],
          total: 0,
        },
      }));
    } finally {
      set((state) => ({ loading: { ...state.loading, reportVoc: false } }));
    }
  },
}));
